import type { LocationQuery, LocationQueryRaw, LocationQueryValue } from 'vue-router'

// Stands for the keyword wherever filter keys are listed: resets, counting, chip rows, the URL.
export const KEYWORD_KEY = 'keyword'

export interface FilterDefinition {
  key: string
  multiple?: boolean
  clearsOnChange?: string[]
  alsoClearsWhenEmptied?: string[]
  // Removing its chip clears only this filter, unlike changing it in the sidebar.
  isChipRemovalIsolated?: boolean
  // The values a URL may set, given the filters before this one. Undefined while they are still
  // loading, so a URL value is kept until it can be checked rather than dropped.
  validValues?: (state: BrowseState) => string[] | undefined
}

export type Refinements = Record<string, string[]>

export interface BrowseState {
  keyword: string
  refinements: Refinements
}

export interface ChipRowDefinition {
  label: string
  keys: string[]
  omitValues?: string[]
}

export interface BrowseStateConfig {
  facets: FilterDefinition[]
  chipRows?: ChipRowDefinition[]
  hasKeywordSearch?: boolean
  normalize?: (state: BrowseState) => BrowseState
  // Params read only on arrival, such as ref, that a filter change leaves out of the URL.
  paramsDroppedOnChange?: string[]
}

export interface FacetOptionSource {
  value: string
  label?: string
  isEnabled?: boolean
  isLocked?: boolean
}

export interface FacetOption {
  value: string
  label: string
  isSelected: boolean
  isEnabled: boolean
  isLocked: boolean
}

export interface Chip {
  key: string
  value: string
}

export interface ChipRow {
  label: string
  chips: Chip[]
}

function isPresent(value: string | null | undefined): value is string {
  return Boolean(value)
}

function queryValues(raw: LocationQueryValue | LocationQueryValue[] | undefined): string[] {
  const values = Array.isArray(raw) ? raw : [raw]
  return values.filter(isPresent)
}

function urlValuesFor(filter: FilterDefinition, query: LocationQuery): string[] {
  const values = queryValues(query[filter.key])
  if (filter.multiple) {
    return values
  }
  return values.slice(0, 1)
}

// A value the filter does not offer (a hand-edited or outdated link) is dropped, so it never
// becomes a chip or a search for something that cannot match.
function acceptedValues(filter: FilterDefinition, urlValues: string[], stateSoFar: BrowseState): string[] {
  const validValues = filter.validValues?.(stateSoFar)
  if (!validValues) {
    return urlValues
  }
  return urlValues.filter((value) => validValues.includes(value))
}

export function enteredKeyword(keyword: string): string {
  return keyword.trim() ? keyword : ''
}

function keywordFromQuery(config: BrowseStateConfig, query: LocationQuery): string {
  if (!config.hasKeywordSearch) {
    return ''
  }
  return enteredKeyword(queryValues(query[KEYWORD_KEY])[0] ?? '')
}

// Filters are read in config order, so each is checked against the filters before it: a
// category is accepted only under the subject the URL validly chose.
function stateFromUrlValues(config: BrowseStateConfig, query: LocationQuery): BrowseState {
  const state: BrowseState = { keyword: keywordFromQuery(config, query), refinements: {} }
  for (const filter of config.facets) {
    state.refinements[filter.key] = acceptedValues(filter, urlValuesFor(filter, query), state)
  }
  return state
}

export function stateFromQuery(config: BrowseStateConfig, query: LocationQuery): BrowseState {
  const state = stateFromUrlValues(config, query)
  if (!config.normalize) {
    return state
  }
  return config.normalize(state)
}

function paramsOwnedByPage(config: BrowseStateConfig): Set<string> {
  const filterParams = config.facets.map((filter) => filter.key)
  if (!config.hasKeywordSearch) {
    return new Set(filterParams)
  }
  return new Set([...filterParams, KEYWORD_KEY])
}

// Params the page does not own, such as beta=true or ref, are carried through untouched
// unless the page drops them on a change.
function paramsNotOwnedByPage(config: BrowseStateConfig, currentQuery: LocationQuery): LocationQueryRaw {
  const ownedParams = paramsOwnedByPage(config)
  const droppedParams = config.paramsDroppedOnChange ?? []
  const isCarriedThrough = (param: string) => !ownedParams.has(param) && !droppedParams.includes(param)
  const unownedEntries = Object.entries(currentQuery).filter(([param]) => isCarriedThrough(param))
  return Object.fromEntries(unownedEntries)
}

export function queryFromState(config: BrowseStateConfig, state: BrowseState, currentQuery: LocationQuery = {}): LocationQueryRaw {
  const query = paramsNotOwnedByPage(config, currentQuery)
  const keyword = enteredKeyword(state.keyword)
  if (config.hasKeywordSearch && keyword) {
    query[KEYWORD_KEY] = keyword
  }
  for (const filter of config.facets) {
    const values = state.refinements[filter.key] ?? []
    if (!values.length) {
      continue
    }
    query[filter.key] = filter.multiple ? values : values[0]
  }
  return query
}

function filterByKey(config: BrowseStateConfig, key: string): FilterDefinition | undefined {
  return config.facets.find((filter) => filter.key === key)
}

function dependentKeysOf(config: BrowseStateConfig, key: string): string[] {
  return filterByKey(config, key)?.clearsOnChange ?? []
}

// A cleared filter clears its own dependents too, so the subject clears the category and,
// through it, the standard. keysToVisit grows as each newly cleared filter adds its dependents.
function keysClearedTransitively(config: BrowseStateConfig, changedKey: string, directlyClearedKeys: string[]): string[] {
  const clearedKeys = new Set<string>()
  const keysToVisit = [...directlyClearedKeys]
  for (const clearedKey of keysToVisit) {
    const isAlreadyHandled = clearedKeys.has(clearedKey) || clearedKey === changedKey
    if (isAlreadyHandled) {
      continue
    }
    clearedKeys.add(clearedKey)
    keysToVisit.push(...dependentKeysOf(config, clearedKey))
  }
  return [...clearedKeys]
}

export function keysClearedBy(config: BrowseStateConfig, key: string): string[] {
  return keysClearedTransitively(config, key, dependentKeysOf(config, key))
}

export function withCleared(state: BrowseState, keys: string[]): BrowseState {
  const refinements = { ...state.refinements }
  for (const key of keys.filter((candidate) => candidate !== KEYWORD_KEY)) {
    refinements[key] = []
  }
  return { keyword: keys.includes(KEYWORD_KEY) ? '' : state.keyword, refinements }
}

function withRefinement(state: BrowseState, key: string, values: string[]): BrowseState {
  return { ...state, refinements: { ...state.refinements, [key]: values } }
}

function selectedValuesFor(state: BrowseState, key: string): string[] {
  if (key !== KEYWORD_KEY) {
    return state.refinements[key] ?? []
  }
  const isKeywordEntered = Boolean(state.keyword)
  if (!isKeywordEntered) {
    return []
  }
  return [state.keyword]
}

export function refine(config: BrowseStateConfig, state: BrowseState, key: string, values: string[]): BrowseState {
  const keysToClear = keysClearedBy(config, key)
  const isBeingEmptied = values.length === 0
  if (isBeingEmptied) {
    const clearedWhenEmptied = filterByKey(config, key)?.alsoClearsWhenEmptied ?? []
    keysToClear.push(...keysClearedTransitively(config, key, clearedWhenEmptied))
  }
  return withRefinement(withCleared(state, keysToClear), key, values)
}

export function toggle(config: BrowseStateConfig, state: BrowseState, key: string, value: string): BrowseState {
  const selectedValues = selectedValuesFor(state, key)
  const isValueSelected = selectedValues.includes(value)
  if (isValueSelected) {
    return refine(config, state, key, selectedValues.filter((selectedValue) => selectedValue !== value))
  }
  const allowsManyValues = filterByKey(config, key)?.multiple
  if (allowsManyValues) {
    return refine(config, state, key, [...selectedValues, value])
  }
  return refine(config, state, key, [value])
}

export function removeChip(config: BrowseStateConfig, state: BrowseState, key: string, value: string): BrowseState {
  if (key === KEYWORD_KEY) {
    return { ...state, keyword: '' }
  }
  const remainingValues = selectedValuesFor(state, key).filter((selectedValue) => selectedValue !== value)
  const isChipRemovalIsolated = filterByKey(config, key)?.isChipRemovalIsolated
  if (isChipRemovalIsolated) {
    return withRefinement(state, key, remainingValues)
  }
  return refine(config, state, key, remainingValues)
}

function toFacetOptionSource(source: string | FacetOptionSource): FacetOptionSource {
  if (typeof source === 'string') {
    return { value: source, label: source }
  }
  return source
}

interface FacetOptionState {
  isLocked: boolean
  isSelected: boolean
  isEnabledBySource: boolean
  hasResults: boolean
}

// Without counts, every option has results as far as the page knows.
function hasResultsFor(value: string, counts: Record<string, number> | undefined): boolean {
  if (!counts) {
    return true
  }
  return (counts[value] ?? 0) > 0
}

// A selected option stays usable so it can be cleared, unless it is premium-locked.
function isFacetOptionEnabled({ isLocked, isSelected, isEnabledBySource, hasResults }: FacetOptionState): boolean {
  if (isLocked) {
    return false
  }
  if (isSelected) {
    return true
  }
  return isEnabledBySource && hasResults
}

function toFacetOption(source: FacetOptionSource, counts: Record<string, number> | undefined, selectedValues: string[]): FacetOption {
  const isSelected = selectedValues.includes(source.value)
  const isLocked = source.isLocked ?? false
  const optionState = { isLocked, isSelected, isEnabledBySource: source.isEnabled ?? true, hasResults: hasResultsFor(source.value, counts) }
  return {
    value: source.value,
    label: source.label ?? source.value,
    isSelected,
    isEnabled: isFacetOptionEnabled(optionState),
    isLocked
  }
}

export function facetOptions(sources: (string | FacetOptionSource)[], counts: Record<string, number> | undefined, selectedValues: string[]): FacetOption[] {
  return sources.map((source) => toFacetOption(toFacetOptionSource(source), counts, selectedValues))
}

function chipsForKey(key: string, state: BrowseState, omittedValues: string[]): Chip[] {
  const shownValues = selectedValuesFor(state, key).filter((value) => !omittedValues.includes(value))
  return shownValues.map((value) => ({ key, value }))
}

function chipsForRow(row: ChipRowDefinition, state: BrowseState): Chip[] {
  const omittedValues = row.omitValues ?? []
  return row.keys.flatMap((key) => chipsForKey(key, state, omittedValues))
}

export function chipRows(config: BrowseStateConfig, state: BrowseState): ChipRow[] {
  const rows = (config.chipRows ?? []).map((row) => ({ label: row.label, chips: chipsForRow(row, state) }))
  const rowsWithChips = rows.filter((row) => row.chips.length > 0)
  return rowsWithChips
}
